import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteResourceCalendar } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ResourceCalendar } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteResourceCalendarDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedCalendar: ResourceCalendar;
  changeLogRoles: string[];
}

export default function DeleteResourceCalendarDialog({
  open,
  handleClose,
  selectedCalendar,
  changeLogRoles,
}: DeleteResourceCalendarDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteResourceCalendar(selectedCalendar, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Resource calendar'),
      });

      queryClient.setQueriesData<ResourceCalendar[] | undefined>(
        { queryKey: [UserKeys.ResourceCalendars] },
        (oldData) => {
          if (oldData) {
            return oldData.filter(
              (calendar) => calendar.resourceId !== selectedCalendar.resourceId
            );
          }
          return [];
        }
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'this resource calendar'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.delete_resource_calendar,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceCalendars,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.DeleteResourceCalendar.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Resource Calendar"
      dialogContent={
        <Box>
          <Typography>
            Delete <strong>{selectedCalendar.resourceName}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Resource Calendar',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
